import { useCombobox } from 'downshift';

import { useReplacePath } from '../../../contexts/RouterContext';
import useCommandPaletteSearch from '../../../hooks/api/commandPalette/useCommandPaletteSearch';
import useDebouncedInputValue from '../../../hooks/useDebouncedInputValue';
import { useFESearchResults } from '../utils';
import type { CommandPaletteSearchFEResult } from '../../../../shared/CommandPalette/types';

import useFilteredResults from './useFilteredResults';
import useNavigableRoutes from './useNavigationRoutes';

const MIN_SEARCH_QUERY_LENGTH = 1;

/**
 * Set up the command palette search functionality using downshift.
 */
function useCommandPalette(onHide: () => void) {
  const replacePath = useReplacePath();
  const {
    value: searchQuery,
    onChange: setSearchQuery,
    debouncedValue: debouncedSearchQuery,
  } = useDebouncedInputValue();

  //  Static routes are always available. This hook just calls the shouldEnable check for each route.
  const staticResults = useNavigableRoutes();
  //  We should never attempt to fetch if the search term is empty.
  const shouldFetchResults = debouncedSearchQuery.trim().length >= MIN_SEARCH_QUERY_LENGTH;
  const {
    data: searchResultsRaw,
    isFetching: isFetchingNewSearchResults,
  } = useCommandPaletteSearch(debouncedSearchQuery, shouldFetchResults);
  const searchResults = useFESearchResults(searchResultsRaw?.results);
  const filteredResults = useFilteredResults(searchQuery, staticResults, searchResults);

  const {
    highlightedIndex,
    getInputProps,
    getMenuProps,
    getItemProps,
    selectItem,
  } = useCombobox<CommandPaletteSearchFEResult>({
    items: filteredResults,
    //  The results need to be serialized, we use `label` which is for sure unique
    itemToString: (item) => item?.label ?? '',
    //  When the user selects an item, we navigate to the route.
    //  This could be clicking, but also by using the keyboard.
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem?.to) {
        replacePath(selectedItem.to);
        onHide();
      }
    },
    onInputValueChange: ({ inputValue: search }) => {
      const searchToLowerCase = search.trim().toLowerCase();
      setSearchQuery(searchToLowerCase);
    },
  });

  return {
    items: filteredResults,
    hasNoResults: filteredResults.length === 0,
    searchQuery,
    highlightedIndex,
    getInputProps: () => {
      return getInputProps({
        onKeyDown: (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            const firstItem = filteredResults[0];
            if (firstItem) {
              selectItem(firstItem);
            }
          }
        },
      });
    },
    getMenuProps,
    getItemProps,

    isFetchingNewSearchResults,
    isAboutToFetchResults: searchQuery.length > 0 && searchQuery !== debouncedSearchQuery,
  };
}

export default useCommandPalette;
